import { useMemo, useState } from "react";
import {
  type Equipment,
  type OperationalLoopReport,
} from "@/lib/api/building";
import { EnvironmentChart } from "./EnvironmentChart";
import { DemandChart, type DemandZoneSeries } from "./DemandChart";
import { RecipeChart } from "./RecipeChart";
import { ImpactChart } from "./ImpactChart";
import {
  applyMarkersOf,
  buildEnvironmentPoints,
  buildImpactPoints,
  buildRecipePoints,
  emptyLoopPoints,
  hasImpactKwh,
  kpiCurveFromSeries,
  notComputableLabelsOf,
  recipeAxisLabels,
  writtenRecipeAxes,
} from "./operational-loop-adapter";
import { SIGNAL_LABELS, focusOf } from "./verification";
import type { HoverHandler } from "./chart-shared";

// Optimointi is device-driven: a Recipe always targets exactly one equipment item
// (`Recipe.equipment_id`, singular — docs/spec/concepts.md → "Recipe source and target"), so
// this section always drills into one selected device — the caller (OptimizationBuildingSection)
// picks it via EquipmentFilter, fetches its loop report, and shows the loop around it: ulkoiset
// vaikuttimet → tilan tarve → resepti → yhteisvaikutus, fixed 24 h history through 24 h forecast
// at the 15-min recipe cadence.
//
// One API call for the whole loop (getOperationalLoop, @/lib/api/building.ts — GET .../
// equipment/{id}/operational-loop, owned by the caller so the recipe card and the Verifiointi
// strip read the same report). The backend buckets every signal, fills real gaps honestly, and
// computes impact. Empty series lists mean unavailable — never a client-side demo curve.
export function OperationalLoopSection({
  equipment,
  report,
  onHover,
}: {
  /** The one selected device this loop drills into (caller filters to `optimization_enabled`
   *  and resolves the "which one" — see OptimizationBuildingSection's `selectedEquipment`). */
  equipment: Equipment;
  /** null until the caller's fetch resolves — every chart shows loading, not a demo. */
  report: OperationalLoopReport | null;
  onHover: HoverHandler;
}) {
  const [showAll, setShowAll] = useState(false);
  const loading = report == null;
  const hasRealEnvironmentData = !!report && report.external.length > 0;
  const hasRealRecipeData = !!report && report.recipe.length > 0;
  const hasImpact = !!report && (report.impact?.length ?? 0) > 0;
  const hasFan = report ? report.impact_empty_reason !== "no_fan_port" : false;

  const environmentPoints =
    report && hasRealEnvironmentData ? buildEnvironmentPoints(report) : [];
  const recipePoints =
    report && hasRealRecipeData ? buildRecipePoints(report) : [];
  const impactPoints = report && hasImpact ? buildImpactPoints(report) : [];
  const hasKwh = hasImpactKwh(report);
  const notComputableLabels = notComputableLabelsOf(report);
  const recipeLabels = recipeAxisLabels(report);
  const grid = report ? emptyLoopPoints(report) : [];

  const demandZones: DemandZoneSeries[] =
    report && report.internal.length > 0
      ? report.internal.map((zone) => ({
          zoneId: zone.zone_id,
          zoneName: zone.zone_name,
          points: grid,
          kpiCurve: kpiCurveFromSeries(zone.kpi ?? []),
        }))
      : [];

  const markers = useMemo(
    () => (report ? applyMarkersOf(report) : []),
    [report],
  );
  const focus = showAll ? null : focusOf(report);
  const focusNames = (report?.focus ?? [])
    .map((n) => SIGNAL_LABELS[n] ?? n)
    .join(", ");
  const notInFocus = (kind: string) =>
    `Tämän laitteen optimointi ei lue ${kind} (KB:n palvelut lukevat: ${focusNames || "—"}). Näytä kaikki signaalit -valinta näyttää ne silti.`;
  const loadingCopy = "Ladataan…";
  const emptyCopy = "Ei vielä historiaa tälle jaksolle.";

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3 px-1">
        <p className="text-[10px] text-muted-foreground">
          {loading
            ? "Ladataan silmukan dataa."
            : `Ulkoiset vaikuttimet: ${hasRealEnvironmentData ? "oikeaa dataa." : "ei vielä historiaa."} Resepti: ${hasRealRecipeData ? "oikeaa dataa." : "ei vielä historiaa."} `}
          {focus
            ? `Näytetään vain tämän laitteen optimoinnin syötteet (${focusNames}).`
            : loading
              ? ""
              : "Näytetään kaikki signaalit."}
        </p>
        {focusOf(report) && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="shrink-0 text-[10px] text-primary hover:underline"
          >
            {showAll
              ? "Näytä vain reseptin syötteet"
              : "Näytä kaikki signaalit"}
          </button>
        )}
      </div>

      <EnvironmentChart
        points={environmentPoints}
        visible={
          focus
            ? {
                price: focus.external.has("electricity_price_eur_kwh"),
                outdoorTemp: focus.external.has("outdoor_temperature_c"),
                solar: focus.external.has("solar_radiation_wm2"),
              }
            : null
        }
        emptyReason={notInFocus("ulkoisia vaikuttimia")}
        unavailable={
          loading ? loadingCopy : hasRealEnvironmentData ? undefined : emptyCopy
        }
        onHover={onHover}
      />

      <DemandChart
        zones={demandZones}
        visible={
          focus
            ? {
                occupancy: focus.internal.has("occupancy"),
                co2: focus.internal.has("co2"),
                temperature: focus.internal.has("temperature"),
              }
            : null
        }
        emptyReason={notInFocus("tilojen sisäisiä signaaleja")}
        unavailable={loading ? loadingCopy : undefined}
        onHover={onHover}
      />

      <RecipeChart
        points={recipePoints}
        equipmentName={equipment.name}
        labels={recipeLabels}
        markers={markers}
        visible={showAll ? null : writtenRecipeAxes(report)}
        unavailable={
          loading ? loadingCopy : hasRealRecipeData ? undefined : emptyCopy
        }
        onHover={onHover}
      />

      <ImpactChart
        points={impactPoints}
        markers={markers}
        loading={loading}
        real={hasImpact}
        hasFan={hasFan}
        hasKwh={hasKwh}
        notComputableLabels={notComputableLabels}
        onHover={onHover}
      />
    </div>
  );
}
